import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { ousAPI } from '../services/api';
import DataSource from '../components/DataSource';
import { formatCreated } from '../utils/formatCreated';
import PaginationControls from '../components/PaginationControls';
import ColumnPicker from '../components/ColumnPicker';
import { useColumnPrefs } from '../hooks/useColumnPrefs';
import {
  fetchOUs,
  addOUOptimistic,
  removeOUOptimistic,
} from '../store/dataSlice';

function OUs() {
  const dispatch = useDispatch();
  const ous = useSelector((state) => state.data.ous.data);
  const ousSource = useSelector((state) => state.data.ous.source);
  const loading = useSelector((state) => state.data.ous.loading);
  const reduxError = useSelector((state) => state.data.ous.error);

  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentOU, setCurrentOU] = useState(null);
  const [formData, setFormData] = useState({ name: '', parent_dn: '' });
  const [searchQuery, setSearchQuery] = useState('');
  // Client-side paging: this endpoint returns every OU in one response,
  // so slicing here is honest — the count below is the real total.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const searchInputRef = useRef(null);

  // Filter OUs based on search query
  const filteredOUs = useMemo(() => {
    if (!searchQuery) return ous;
    const query = searchQuery.toLowerCase();
    return ous.filter(ou =>
      (ou.name && ou.name.toLowerCase().includes(query)) ||
      (ou.dn && ou.dn.toLowerCase().includes(query))
    );
  }, [ous, searchQuery]);

  // Paging is derived from the FILTERED list, so a search narrows the pages
  // rather than leaving empty ones behind.
  const totalPages = Math.max(1, Math.ceil(filteredOUs.length / pageSize));
  const pagedOUs = filteredOUs.slice((page - 1) * pageSize, page * pageSize);

  // The data columns, as data. Actions is deliberately NOT here: it is not a
  // field, it must always be last, and hiding it would strip the page of every
  // control with no way to get them back.
  const OU_COLUMNS = React.useMemo(() => [
    { key: 'name', label: 'Name', render: (ou) => ou.name },
    {
      key: 'dn',
      label: 'Distinguished Name',
      sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
      render: (ou) => ou.dn,
    },
    {
      key: 'created',
      label: 'Created',
      sx: { whiteSpace: 'nowrap' },
      render: (ou) => formatCreated(ou.whenCreated),
    },
    { key: 'description', label: 'Description', render: (ou) => ou.description || '-' },
  ], []);
  const { ordered, visibleColumns, toggle, move, reset } =
    useColumnPrefs('cols.ous', OU_COLUMNS);

  useEffect(() => {
    // Load cached data or fetch if cache expired
    dispatch(fetchOUs()).then(() => {
      setInitialLoadDone(true);
    });

    // Listen for sudo session established event
    const handleSudoSession = () => {
      // Force refresh when sudo session established
      dispatch(fetchOUs(true));
    };

    window.addEventListener('sudo-session-established', handleSudoSession);

    return () => {
      window.removeEventListener('sudo-session-established', handleSudoSession);
    };
  }, [dispatch]);

  const handleRefresh = () => {
    // Force refresh bypassing cache
    dispatch(fetchOUs(true));
  };

  const handleOpenDialog = (mode, ou = null) => {
    setDialogMode(mode);
    setCurrentOU(ou);
    if (mode === 'create') {
      setFormData({ name: '', parent_dn: '' });
    } else if (mode === 'rename' && ou) {
      setFormData({ name: ou.name || '', dn: ou.dn });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentOU(null);
    setFormData({ name: '', parent_dn: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name || formData.name.trim() === '') {
      setError('OU name is required');
      return;
    }
    if (!/^[a-zA-Z0-9_\s-]+$/.test(formData.name)) {
      setError('OU name can only contain letters, numbers, spaces, hyphens, and underscores');
      return;
    }

    try {
      if (dialogMode === 'create') {
        const response = await ousAPI.create({
          name: formData.name,
          parent_dn: formData.parent_dn || undefined,
        });
        // Optimistic update
        dispatch(addOUOptimistic({
          name: formData.name,
          dn: response.data.dn || `OU=${formData.name}`,
          parent_dn: formData.parent_dn,
        }));
      } else if (dialogMode === 'rename') {
        await ousAPI.rename(currentOU.dn, formData.name);
      }
      handleCloseDialog();
      // Refresh in background to ensure data is accurate
      dispatch(fetchOUs(true));
    } catch (err) {
      setError(err.response?.data?.error || 'Operation failed');
      // Revert optimistic update on error
      dispatch(fetchOUs(true));
    }
  };

  const handleDelete = async (dn) => {
    if (!window.confirm(`Are you sure you want to delete this OU?\n${dn}`)) {
      return;
    }
    try {
      // Optimistic update
      dispatch(removeOUOptimistic(dn));
      await ousAPI.delete(dn);
      // Refresh in background
      dispatch(fetchOUs(true));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete OU');
      // Revert on error
      dispatch(fetchOUs(true));
    }
  };

  // Only show full loading spinner on initial load
  if (loading && !initialLoadDone) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3,
                 flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4">Organizational Units</Typography>
          <DataSource source={ousSource} sx={{ mt: 0.5 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ColumnPicker
            columns={ordered}
            onToggle={toggle}
            onMove={move}
            onReset={reset}
          />
          <IconButton onClick={handleRefresh} sx={{ mr: 1 }} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Create OU
          </Button>
        </Box>
      </Box>

      {(error || reduxError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            inputRef={searchInputRef}
            placeholder="Search OUs by name or DN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {searchQuery && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Found {filteredOUs.length} OU{filteredOUs.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </Typography>
          </Box>
        )}
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {visibleColumns.map((c) => (
                <TableCell key={c.key}>{c.label}</TableCell>
              ))}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOUs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery
                      ? `No organizational units matching "${searchQuery}"`
                      : 'No organizational units found. Click "Create OU" to add a new OU.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pagedOUs.map((ou) => (
              <TableRow key={ou.dn}>
                {visibleColumns.map((c) => (
                  <TableCell key={c.key} sx={c.sx}>{c.render(ou)}</TableCell>
                ))}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog('rename', ou)}
                    title="Rename"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(ou.dn)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationControls
        count={filteredOUs.length}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={(e, p) => setPage(p)}
        onPageSizeChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create Organizational Unit' : 'Rename OU'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label={dialogMode === 'create' ? 'OU Name' : 'New Name'}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              helperText={
                dialogMode === 'create'
                  ? 'Example: Sales, HR, IT'
                  : 'Enter the new name for this OU'
              }
            />
            {dialogMode === 'create' && (
              <Autocomplete
                options={[
                  { name: 'Domain Root', dn: '' },
                  ...ous.map(ou => ({ name: ou.name, dn: ou.dn }))
                ]}
                getOptionLabel={(option) => option.name ? `${option.name} (${option.dn || 'root'})` : ''}
                value={ous.find(ou => ou.dn === formData.parent_dn) || { name: 'Domain Root', dn: '' }}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, parent_dn: newValue ? newValue.dn : '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent DN (optional)"
                    margin="normal"
                    helperText="Select a parent OU or leave as Domain Root"
                  />
                )}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OUs;
